// src/store/thunks/cancellationThunks.js

import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import cancellationService from "../../../services/jewelleryPlan/cancellationService";

// ==================== ELIGIBILITY & QUOTES ====================

// Check cancellation eligibility
export const checkCancellationEligibilityThunk = createAsyncThunk(
  "cancellations/checkEligibility",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await cancellationService.checkEligibility(planId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get cancellation quote
export const getCancellationQuoteThunk = createAsyncThunk(
  "cancellations/getQuote",
  async ({ planId, reasonCategory }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.getCancellationQuote(
        planId,
        reasonCategory
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== CANCELLATION PROCESSING ====================

// Process cancellation
export const processCancellationThunk = createAsyncThunk(
  "cancellations/process",
  async ({ planId, cancellationData }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.processCancellation(
        planId,
        cancellationData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get cancellation status
export const getCancellationStatusThunk = createAsyncThunk(
  "cancellations/getStatus",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await cancellationService.getCancellationStatus(planId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== APPROVAL WORKFLOW ====================

// Get pending cancellations
export const getPendingCancellationsThunk = createAsyncThunk(
  "cancellations/getPending",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await cancellationService.getPendingCancellations(
        filters
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Approve cancellation request
export const approveCancellationRequestThunk = createAsyncThunk(
  "cancellations/approveRequest",
  async ({ cancellationRequestId, approvalData }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.approveCancellationRequest(
        cancellationRequestId,
        approvalData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== REFUND MANAGEMENT ====================

// ✅ NEW: List all refunds
export const listRefundsThunk = createAsyncThunk(
  "cancellations/listRefunds",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await cancellationService.listRefunds(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get refund status
export const getRefundStatusThunk = createAsyncThunk(
  "cancellations/getRefundStatus",
  async (refundRequestId, { rejectWithValue }) => {
    try {
      const response = await cancellationService.getRefundStatus(
        refundRequestId
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Update refund status
export const updateRefundStatusThunk = createAsyncThunk(
  "cancellations/updateRefundStatus",
  async ({ refundRequestId, statusData }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.updateRefundStatus(
        refundRequestId,
        statusData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ NEW: Approve refund
export const approveRefundThunk = createAsyncThunk(
  "cancellations/approveRefund",
  async ({ refundRequestId, approvalData }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.approveRefund(
        refundRequestId,
        approvalData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ NEW: Complete refund
export const completeRefundThunk = createAsyncThunk(
  "cancellations/completeRefund",
  async ({ refundRequestId, completionData }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.completeRefund(
        refundRequestId,
        completionData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== COLLECTION MANAGEMENT ====================

// ✅ NEW: List all collections
export const listCollectionsThunk = createAsyncThunk(
  "cancellations/listCollections",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await cancellationService.listCollections(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get collection status
export const getCollectionStatusThunk = createAsyncThunk(
  "cancellations/getCollectionStatus",
  async (collectionRequestId, { rejectWithValue }) => {
    try {
      const response = await cancellationService.getCollectionStatus(
        collectionRequestId
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ NEW: Update collection status
export const updateCollectionStatusThunk = createAsyncThunk(
  "cancellations/updateCollectionStatus",
  async ({ collectionRequestId, statusData }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.updateCollectionStatus(
        collectionRequestId,
        statusData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Record collection payment
export const recordCollectionPaymentThunk = createAsyncThunk(
  "cancellations/recordCollectionPayment",
  async ({ collectionRequestId, paymentData }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.recordCollectionPayment(
        collectionRequestId,
        paymentData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ NEW: Waive collection (write-off)
export const waiveCollectionThunk = createAsyncThunk(
  "cancellations/waiveCollection",
  async ({ collectionRequestId, waiveData }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.waiveCollection(
        collectionRequestId,
        waiveData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ NEW: Resolve collection
export const resolveCollectionThunk = createAsyncThunk(
  "cancellations/resolveCollection",
  async ({ collectionRequestId, resolutionData }, { rejectWithValue }) => {
    try {
      const response = await cancellationService.resolveCollection(
        collectionRequestId,
        resolutionData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== STATISTICS ====================

// ✅ NEW: Get cancellation statistics
export const getCancellationStatsThunk = createAsyncThunk(
  "cancellations/getStats",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await cancellationService.getCancellationStats(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ NEW: Get refund statistics
export const getRefundStatsThunk = createAsyncThunk(
  "cancellations/getRefundStats",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await cancellationService.getRefundStats(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ NEW: Get collection statistics
export const getCollectionStatsThunk = createAsyncThunk(
  "cancellations/getCollectionStats",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await cancellationService.getCollectionStats(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== UTILITY ACTIONS ====================

// Clear cancellation error
export const clearCancellationError = createAction(
  "cancellations/clearCancellationError"
);

// ✅ NEW: Clear refund data
export const clearRefundData = createAction("cancellations/clearRefundData");

// ✅ NEW: Clear collection data
export const clearCollectionData = createAction(
  "cancellations/clearCollectionData"
);

// ✅ NEW: Clear statistics data
export const clearStatsData = createAction("cancellations/clearStatsData");

// ✅ NEW: Reset cancellation state
export const resetCancellationState = createAction("cancellations/resetState");
